import type { PixelConfig } from '@/config';
import { BaseEventBuilder } from '@/events/builder';
import { ConsolePixelLogger, type PixelLogger } from '@/logger';
import {
  AttributionServiceImpl,
  DEFAULT_ATTRIBUTION_CONFIG,
} from '@/services/attribution';
import { BrowserMetadataServiceImpl } from '@/services/browser-metadata/BrowserMetadataService';
import { DispatcherServiceImpl } from '@/services/dispatcher/DispatcherService';
import { FingerprintServiceImpl } from '@/services/fingerprint/FingerprintService';
import { createStorageAdapter, StorageServiceImpl } from '@/services/storage';
import type { StoragePersistence } from '@/services/storage/StorageFactory';
import { CommerceService } from '@/services/commerce';
import { SessionServiceImpl } from '@/services/session/SessionService';
import type { PixelRuntimeServices } from '@/services/types';
import { VisitorServiceImpl } from '@/services/visitor/VisitorService';
import { resolveCustomerFieldMappings } from '@/config/shared/commerce';
import { CustomerExtractor } from '@/modules/conversion/commerce/CustomerExtractor';
import { UrlContextService } from '@/modules/core/url-context';
import {
  CustomerIdentityObserver,
  CustomerIdentityService,
} from '@/services/customer';

import { ModuleRegistry } from './ModuleRegistry';
import type { PixelRuntimeContext } from './PixelRuntimeContext';
import type { ModuleName } from './shared/module';
import type { PixelModule } from './types';

export class PixelRuntime {
  private initialized = false;

  private customerObserver: CustomerIdentityObserver | null = null;

  private readonly initializedModules: PixelModule[] = [];

  private readonly registry: ModuleRegistry;

  private readonly config: PixelConfig;

  private readonly fallbackLogger: ConsolePixelLogger;

  private context: PixelRuntimeContext | null = null;

  constructor(config: PixelConfig) {
    this.config = config;
    this.registry = new ModuleRegistry();
    this.fallbackLogger = new ConsolePixelLogger(config.app.debug === true);
  }

  register(module: PixelModule): void {
    this.registry.register(module);
  }

  async init(): Promise<void> {
    if (this.initialized) {
      this.getLogger().debug('[PixelRuntime] Runtime already initialized');
      return;
    }

    this.fallbackLogger.debug('[PixelRuntime] Initializing runtime');

    this.context = this.createContext();
    this.context.services.attribution.init();
    this.startCustomerObserver();

    for (const module of this.registry.getAll()) {
      if (!this.isModuleEnabled(module)) {
        this.fallbackLogger.debug(
          `[PixelRuntime] Skipping disabled module: ${module.name}`
        );
        continue;
      }

      this.fallbackLogger.debug(
        `[PixelRuntime] Initializing module: ${module.name}`
      );

      try {
        await module.init(this.context);
        this.initializedModules.push(module);
      } catch (error) {
        this.fallbackLogger.error(
          `[Pixel] Failed to initialize module "${module.name}"`,
          error
        );
      }
    }

    this.initialized = true;
  }

  async destroy(): Promise<void> {
    if (!this.initialized) return;

    const logger = this.getLogger();

    for (let index = this.initializedModules.length - 1; index >= 0; index--) {
      const module = this.initializedModules[index];

      logger.debug(`[PixelRuntime] Destroying module: ${module.name}`);

      try {
        await module.destroy?.();
      } catch (error) {
        logger.error(
          `[Pixel] Failed to destroy module "${module.name}"`,
          error
        );
      }
    }

    this.customerObserver?.destroy();
    this.customerObserver = null;
    this.context?.services.customerIdentity?.clear();
    this.context?.services.attribution.destroy();
    this.context?.services.dispatcher.clear();

    this.initialized = false;
    this.initializedModules.length = 0;
    this.context = null;

    this.fallbackLogger.debug('[PixelRuntime] Runtime destroyed');
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /** @internal For playground and development only. Not part of the stable public API. */
  getDebugServices(): PixelRuntimeServices | null {
    return this.context?.services ?? null;
  }

  /** @internal Read-only diagnostics — names of modules that completed init. */
  getInitializedModuleNames(): readonly string[] {
    return this.initializedModules.map(m => m.name);
  }

  /** @internal Registered module names (all registered, regardless of enabled). */
  getRegisteredModuleNames(): readonly string[] {
    return this.registry.getAll().map(m => m.name);
  }

  private createServices(logger: PixelLogger): PixelRuntimeServices {
    const { namespace, prefix, persistence } = this.config.storage;

    const visitorStorage = this.createStorageService(
      namespace,
      prefix,
      persistence?.visitor
    );
    const sessionStorage = this.createStorageService(
      namespace,
      prefix,
      persistence?.session
    );
    const fingerprintStorage = this.createStorageService(
      namespace,
      prefix,
      persistence?.attribution
    );
    const attributionStorage = this.createStorageService(
      namespace,
      prefix,
      persistence?.attribution ?? 'localStorage'
    );
    const storage = this.createStorageService(namespace, prefix, 'memory');
    const browserMetadata = new BrowserMetadataServiceImpl();

    const attribution = new AttributionServiceImpl(
      attributionStorage,
      this.config.attribution ?? DEFAULT_ATTRIBUTION_CONFIG
    );

    const urlContextStorage = this.createStorageService(
      namespace,
      prefix,
      persistence?.attribution ?? 'localStorage'
    );
    const urlContext = new UrlContextService(
      this.config.modules.core.urlContext,
      urlContextStorage
    );

    const customerIdentity = new CustomerIdentityService();

    const eventBuilder = new BaseEventBuilder(
      browserMetadata,
      attribution,
      urlContext,
      customerIdentity
    );

    const visitor = new VisitorServiceImpl(visitorStorage, this.config);
    const session = new SessionServiceImpl(sessionStorage, this.config);
    const fingerprint = new FingerprintServiceImpl(
      fingerprintStorage,
      this.config,
      browserMetadata
    );
    const dispatcher = new DispatcherServiceImpl(logger);
    const commerce = new CommerceService(this.config.commerce);

    return {
      storage,
      visitor,
      session,
      fingerprint,
      dispatcher,
      browserMetadata,
      eventBuilder,
      attribution,
      commerce,
      urlContext,
      customerIdentity,
    };
  }

  private startCustomerObserver(): void {
    const identity = this.context?.services.customerIdentity;
    if (!identity) return;
    this.customerObserver = new CustomerIdentityObserver(
      identity,
      new CustomerExtractor({
        fieldMappings: resolveCustomerFieldMappings(
          this.config.commerce.customer,
          this.config.commerce.mappings?.fields
        ),
      })
    );
    this.customerObserver.start();
  }

  private createStorageService(
    namespace: string,
    prefix: string | undefined,
    persistence: StoragePersistence | undefined
  ): StorageServiceImpl {
    return new StorageServiceImpl(
      createStorageAdapter(persistence),
      namespace,
      prefix
    );
  }

  private createContext(): PixelRuntimeContext {
    return {
      config: this.config,
      logger: this.fallbackLogger,
      services: this.createServices(this.fallbackLogger),
    };
  }

  private getLogger(): PixelLogger {
    return this.context?.logger ?? this.fallbackLogger;
  }

  private isModuleEnabled(module: PixelModule): boolean {
    if (module.name === 'diagnostics') return true;

    const enabledModules: Record<
      Exclude<ModuleName, 'diagnostics'>,
      boolean
    > = {
      privacy: this.config.modules.privacy.enabled,
      core: this.config.modules.core.enabled,
      behavior: this.config.modules.behavior.enabled,
      conversion: this.config.modules.conversion.enabled,
      transport: this.config.transport.enabled,
      integrations: this.config.modules.integrations.enabled,
    };

    return enabledModules[module.name];
  }
}
